import  { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import '../../styles/common.css';
import '../../styles/dashboard.css';
import '../../styles/test-case-optimization.css';
import '../../styles/tabs.css';
import '../../styles/projects.css';
import { AppShell } from '../shell';
import { useNavigate, useLocation } from 'react-router';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import {
  buildWorkspaceDashboardPath,
  isWorkspaceDashboardHome,
} from '../../utils/workspaceRouteUtils';
import { toast } from 'react-toast';
import axiosInstance from '../../api/axiosInstance';
import { useWorkspaceList, isUserAuthenticated, useUpdateWorkspace } from '../../hooks/useWorkspace';
import DashboardRoutes from './dashboard.routes';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorBoundary } from '../common';
import ProjectOverview from './ProjectOverview';
import WorkspaceHome from './WorkspaceHome';


const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryClient = useQueryClient();
  const { currentWorkspace, selectWorkspace, userWorkspaces, setUserWorkspaces, isWorkspaceLoading, getWorkspacePath ,clearWorkspace} = useWorkspace();
  const { data: workspaceFromApi = [] } = useWorkspaceList();

  const workspacesData = Array.isArray(workspaceFromApi)
    ? workspaceFromApi
    : Array.isArray(workspaceFromApi?.data)
      ? workspaceFromApi.data
      : [];

 // Keep context workspace list in sync (AppShell no longer uses Navbar for this)
 useEffect(() => {
   if (workspacesData.length > 0 && typeof setUserWorkspaces === 'function') {
     setUserWorkspaces(workspacesData);
   }
 }, [workspacesData, setUserWorkspaces]);

 const lastUsedWorkspace = workspacesData[0] ?? null;


  const isDashboardHome = useCallback(() => {
    return isWorkspaceDashboardHome(location.pathname);
  }, [location.pathname]);

  const { clearProject, selectProject, selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL, syncWorkspaceFromURL, isWorkspaceRoute } = useWorkspaceRouting();
  const { mutate: updateWorkspaceFn } = useUpdateWorkspace();

  const urlProjectId = getProjectIdFromURL();
  const activeProjectId = urlProjectId || selectedProject?.id;
  const activeProjectName = selectedProject?.name || '';


  // console.log('workspacesData from api -->',workspacesData)
  
  // Helper function to navigate to workspace-scoped routes
const navigateTo = useCallback((path = '') => {
  let workspace = currentWorkspace;

  if (!workspace?.id) {
    const storedWorkspace = localStorage.getItem('selectedWorkspace');

    if (storedWorkspace) {
      try {
        workspace = JSON.parse(storedWorkspace);
      } catch (e) {
        console.error('Failed to parse stored workspace');
      }
    }
  }

  if (!workspace?.id) {
    navigate('/dashboard/all-workspaces');
    return;
  }

  const pathBase = String(path || '').split('?')[0].replace(/^\/+|\/+$/g, '');
  // Intentional leave-project destinations (workspace sidebar). Overview never uses these.
  const leaveProjectUrl = new Set([
    'projects',
    'all-workspaces',
    'workspace',
    'workspace-home',
  ]).has(pathBase);
  const clearSelection = new Set([
    'all-workspaces',
    'workspace',
    'workspace-home',
  ]).has(pathBase);
  const urlPid = getProjectIdFromURL();

  let projectId = null;
  if (leaveProjectUrl) {
    // All Projects: drop URL project (workspace nav) but keep last selection in top bar.
    // Workspace Home / All Workspaces: clear selection too.
    if (clearSelection && typeof clearProject === 'function') {
      clearProject();
    }
    projectId = null;
  } else {
    projectId = urlPid || null;
  }

  const subPath =
    pathBase === 'workspace-home' || pathBase === 'workspace' || pathBase === 'overview'
      ? ''
      : path;

  navigate(buildWorkspaceDashboardPath(workspace.id, projectId, subPath));
}, [currentWorkspace, navigate, getProjectIdFromURL, clearProject]);

  
  const [selectedTestSuiteProjectId, setSelectedTestSuiteProjectId] = useState(null);
  const [selectedTestSuite, setSelectedTestSuite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentProjects, setRecentProjects] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [checkTab, setChecktab] = useState('test-Cases');
  const [user, setUser] = useState(null);
  const [workspaceValidated, setWorkspaceValidated] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);

  // Refs to prevent duplicate API calls and track state
  const isFetchingRef = useRef(false);
  const currentFetchContextRef = useRef(null);
  const lastFetchedContextRef = useRef(null);
  const isOnDashboardRef = useRef(false);

useEffect(() => {
  const urlProjectId = getProjectIdFromURL();
  const isLegacyDashboard = location.pathname === '/dashboard';
  const isLegacyDashboardChild =
    location.pathname.startsWith('/dashboard/') &&
    location.pathname !== '/dashboard/all-workspaces';

  // Upgrade bare /dashboard/* (except all-workspaces) to /workspace_X/dashboard/*
  if (isLegacyDashboardChild) {
    let workspace = currentWorkspace;
    if (!workspace?.id) {
      try {
        workspace = JSON.parse(localStorage.getItem('selectedWorkspace') || 'null');
      } catch {
        workspace = null;
      }
    }
    if (workspace?.id) {
      const rest = location.pathname.replace(/^\/dashboard\/?/, '');
      navigate(
        `${buildWorkspaceDashboardPath(workspace.id, null, rest)}${location.search || ''}`,
        { replace: true }
      );
      return;
    }
  }

  // Only auto-restore last workspace/project from the bare /dashboard entry.
  // Never hijack an explicit /workspace_X/... route the user just selected.
  if (!isLegacyDashboard) return;
  if (!workspaceFromApi?.data?.length && !workspaceFromApi?.last_workspace_data) return;

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const lastWorkspace = workspaceFromApi.last_workspace_data;

  if (lastWorkspace?.user_id && currentUser?.userId &&
      lastWorkspace.user_id !== currentUser.userId) {
    return;
  }

  const lastProject = workspaceFromApi.last_project_data;

  if (!lastWorkspace?.id || !lastProject?.project_id) {
    navigate('/dashboard/all-workspaces', { replace: true });
    return;
  }

  selectWorkspace(lastWorkspace);
  selectProject(String(lastProject.project_id), lastProject.project_name);

  const targetPath = buildWorkspaceDashboardPath(
    lastWorkspace.id,
    lastProject.project_id
  );

  if (location.pathname !== targetPath) {
    navigate(targetPath, { replace: true });
  }
}, [
  location.pathname,
  location.search,
  workspaceFromApi,
  navigate,
  selectWorkspace,
  selectProject,
  getProjectIdFromURL,
  currentWorkspace,
]);


  const [dashboardData, setDashboardData] = useState({
    counts: {
      projects: 0,
      test_suites: 0,
      test_plans: 0,
      test_cases: 0,
    },
    recentProjects: [],
    metrics: {
      test_cases: { current: 0, previous: 0, growth_pct: 0 },
      pass_rate: { current: 0, previous: 0, growth_pct: 0 },
      avg_execution_time_sec: { current: 0, previous: 0, growth_pct: 0 },
      test_coverage: { current: 0, previous: 0, growth_pct: 0 },
    },
    plan_execution_times: {
      labels: [],
      data: [],
    },
    testExecutionTimeData: {
      labels: [],
      data: [],
    },
    static: {
      test_runs: 0,
      reports: 0,
      total_test_passed: 0,
      total_test_failed: 0,
      total_test_skipped: 0,
      tests_created: 0,
      tests_executed: 0,
      tests_passed: 0,
    },
  });

  // Validate workspace on mount or when route changes
    useEffect(() => {
      const validateAndSyncWorkspace = async () => {
        if (!isUserAuthenticated() || sessionStorage.getItem('isLoggingOut') === 'true') {
          return;
        }

        try {
          setWorkspaceValidated(false);

          const normalize = (id) => String(id);

          const getStoredWorkspace = () => {
            try {
              const stored = localStorage.getItem('selectedWorkspace');
              return stored ? JSON.parse(stored) : null;
            } catch {
              return null;
            }
          };

          const isWorkspaceBasedRoute = isWorkspaceRoute();

          if (isWorkspaceBasedRoute) {
            // This is a workspace-specific route (/:workspaceId/dashboard/*)
            const workspaceIdFromURL = getWorkspaceIdFromURL();

            if (!workspaceIdFromURL) {
              // Invalid URL format
              if (!isUserAuthenticated() || sessionStorage.getItem('isLoggingOut') === 'true') return;
              toast.info('Please select a workspace.');
              navigate('/dashboard/all-workspaces');
              return;
            }

            // Validate workspace exists in user's workspace list
            const list = (userWorkspaces?.length ? userWorkspaces : workspacesData) || [];
            const workspaceExists = list.some(
              (w) => normalize(w.id) === normalize(workspaceIdFromURL)
            );

            if (!workspaceExists) {
              const storedWorkspace = getStoredWorkspace();
              if (
                storedWorkspace &&
                normalize(storedWorkspace.id) === normalize(workspaceIdFromURL)
              ) {
                selectWorkspace(storedWorkspace);
                setWorkspaceValidated(true);
                return;
              }

              // URL has a workspace id — trust it rather than bouncing to all-workspaces
              // while the list is still loading or slightly out of sync.
              if (list.length === 0) {
                selectWorkspace({
                  id: workspaceIdFromURL,
                  workspace_name: `Workspace ${workspaceIdFromURL}`,
                });
                setWorkspaceValidated(true);
                return;
              }

              if (!isUserAuthenticated() || sessionStorage.getItem('isLoggingOut') === 'true') return;
              toast.info('Please select a workspace.');
              navigate('/dashboard/all-workspaces');
              return;
            }

            // Find and select the workspace
            const workspace = list.find(
              (w) => normalize(w.id) === normalize(workspaceIdFromURL)
            );
            if (workspace && (!currentWorkspace || normalize(currentWorkspace.id) !== normalize(workspace.id))) {
              selectWorkspace(workspace);
            }
          }

          setWorkspaceValidated(true);
        } catch (error) {
          console.error('Workspace validation error:', error);
          if (!isUserAuthenticated() || sessionStorage.getItem('isLoggingOut') === 'true') return;
          toast.error('Workspace validation failed');
          navigate('/dashboard/all-workspaces');
        }
      };


      if (!isWorkspaceLoading) {
        validateAndSyncWorkspace();
      }
    }, [
  location.pathname,
  userWorkspaces,
  workspacesData,
  currentWorkspace?.id,
  isWorkspaceLoading
]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  // Reset project selection and other state when workspace or project changes
  useEffect(() => {
    const urlWorkspaceId = getWorkspaceIdFromURL();
    const urlProjectId = getProjectIdFromURL();

    if (currentWorkspace?.id || urlWorkspaceId) {
      setSelectedTestSuite(null);
      setSelectedTestSuiteProjectId(null);

      const effectiveWorkspaceId = urlWorkspaceId || currentWorkspace?.id;
      const fetchContext = `${effectiveWorkspaceId}-${urlProjectId || ''}`;

      if (currentFetchContextRef.current !== fetchContext) {
        currentFetchContextRef.current = fetchContext;
        lastFetchedContextRef.current = null;
        setDataFetched(false);

        if (!urlProjectId && !selectedProject?.id) {
          clearProject();
        }
      }
    }
  }, [currentWorkspace?.id, clearProject, getWorkspaceIdFromURL, getProjectIdFromURL, location.pathname, selectedProject?.id]);

  useEffect(() => {
    const projectId = getProjectIdFromURL();
    if (!projectId) return;

    if (String(selectedProject?.id) === String(projectId) && selectedProject?.name) {
      return;
    }

    try {
      const storedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
      if (String(storedProject.id) === String(projectId) && storedProject.name) {
        selectProject(projectId, storedProject.name);
        return;
      }
    } catch {
      // Ignore invalid localStorage data
    }
  }, [getProjectIdFromURL, location.pathname, selectProject, selectedProject?.id, selectedProject?.name]);

  const fetchAllData = useCallback(async () => {
    // Prevent concurrent API calls
    if (isFetchingRef.current) {
      return;
    }

    // Get workspace ID - use URL param if available (more reliable than context)
    let workspaceId = currentWorkspace?.id;
    const urlWorkspaceId = getWorkspaceIdFromURL();
    if (urlWorkspaceId) {
      workspaceId = urlWorkspaceId;
    }

    const urlProjectId = getProjectIdFromURL();
    const fetchContext = `${workspaceId}-${urlProjectId || ''}`;

    // Prevent fetching again for the same workspace/project context
    if (lastFetchedContextRef.current === fetchContext && dataFetched) {
      return;
    }

    // The project dashboard loads its own sections; this data only feeds Workspace Home.
    if (urlProjectId) {
      lastFetchedContextRef.current = fetchContext;
      setDataFetched(true);
      setIsLoading(false);
      setLoading(false);
      setLoadingProjects(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setLoading(true);
      setLoadingProjects(true);

      const authDataString = localStorage.getItem('user');
      if (!authDataString) throw new Error('User not authenticated');

      const queryParams = new URLSearchParams();
      if (workspaceId) {
        queryParams.set('workspace_id', workspaceId);
      }
      // if (urlProjectId) {
      //   queryParams.set('project_id', urlProjectId);
      // }

      const dashboardData = await axiosInstance.get(
        `/projects/dashboard_data${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      );
      const transformedProjects = dashboardData.recent_projects.map(project => ({
        id: project.project_id,
        name: project.project_name,
        status: project.status,
       updatedAt: project.updated_at,
        createdAt: project.updated_at  
      }));

      setRecentProjects(transformedProjects);

      // Fetch active projects for table with workspace_id
      const activeProjectsResponse = await axiosInstance.get(`/projects/list_project?page=1&page_size=50&pro_status=active&workspace_id=${workspaceId || ''}`);
      const transformedActiveProjects = activeProjectsResponse.projects.map(project => ({
        id: project.project_id,
        name: project.project_name,
        description: project.description || 'No description',
        type: project.project_type || 'None',
        status: project.status || 'active',
        progress: project.progress || 0,
        testCases: project.test_case_count || 0,
        passRate: project.pass_rate || 0,
        createdBy: project.created_by?.name || 'Unknown',
        createdAt: project.created_at,
        updatedAt: project.updated_at
      }));

      setActiveProjects(transformedActiveProjects);

      const testStatusData = [
        dashboardData.static.tests_created, 
        dashboardData.static.total_test_passed, 
        dashboardData.static.total_test_failed, 
        dashboardData.static.total_test_pending, 
      ];

      const planExecutionTimes = Array.isArray(dashboardData.plan_execution_times)
        ? dashboardData.plan_execution_times
        : [];
      const executionHistory = Array.isArray(dashboardData.test_plan_execution_history)
        ? dashboardData.test_plan_execution_history
        : [];
      const historyTimeByPlanId = executionHistory.reduce((acc, history) => {
        if (history?.test_plan_id != null) {
          acc[history.test_plan_id] = Number(history.average_execution_time_sec) || 0;
        }
        return acc;
      }, {});

      // Prefer live execution_time_sec; fall back to history averages when current time is 0
      const executionTimeLabels = planExecutionTimes.map((plan) => plan.test_plan_name);
      const executionTimeValues = planExecutionTimes.map((plan) => {
        const currentTime = Number(plan.execution_time_sec) || 0;
        if (currentTime > 0) return currentTime;
        return historyTimeByPlanId[plan.test_plan_id] || 0;
      });

      const testExecutionTimeData = {
        labels: executionTimeLabels,
        data: executionTimeValues,
      };

      setDashboardData({
        counts: dashboardData.counts,
        recentProjects: transformedProjects,
        metrics: dashboardData.metrics,
        plan_execution_times: {
          labels: executionTimeLabels,
          data: executionTimeValues,
        },
        static: dashboardData.static,
        // Chart dashboardData
        testStatusData,
        testProgressData : dashboardData.test_progress || [],
        testExecutionTimeData,
        testPercentages: {
          total_test_cases: dashboardData.counts.test_cases,
          percent_change: dashboardData.metrics.test_cases.growth_pct
        },
        testSummaryMetrics: {
          current_month_pass_rate: dashboardData.metrics.pass_rate.current,
          pass_rate_change: dashboardData.metrics.pass_rate.growth_pct,
          current_month_avg_execution_time_sec: dashboardData.metrics.avg_execution_time_sec.current,
          execution_time_change_percent: dashboardData.metrics.avg_execution_time_sec.growth_pct,
          test_coverage: dashboardData.metrics.test_coverage.current,
          coverage_change: dashboardData.metrics.test_coverage.growth_pct
        }
      });

      // Mark this workspace/project context as fetched
      lastFetchedContextRef.current = fetchContext;
      setDataFetched(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      // toast.error("Failed to load dashboard data");
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
      setLoading(false);
      setLoadingProjects(false);
    }
  }, [currentWorkspace?.id, dataFetched, getWorkspaceIdFromURL, getProjectIdFromURL]);

  // Track when user is on dashboard route to reset data when returning
  useEffect(() => {
    if (isDashboardHome()) {
      isOnDashboardRef.current = true;
    } else {
      // User navigated away from dashboard - reset dataFetched when they return
      isOnDashboardRef.current = false;
      setDataFetched(false);
    }
  }, [location.pathname,isDashboardHome]);

  // Only fetch data when:
  // 1. Workspace is validated
  // 2. We're on the dashboard route
  // 3. Data hasn't been fetched yet for this workspace
  useEffect(() => {    
    if (!isDashboardHome()) {
      return; // Don't fetch if not on dashboard route
    }

    // Get workspace ID from URL (source of truth)
    const urlWorkspaceId = getWorkspaceIdFromURL();
    const workspaceId = urlWorkspaceId || currentWorkspace?.id;

    if (workspaceValidated && workspaceId && !dataFetched) {
      fetchAllData();
    }
  }, [workspaceValidated, dataFetched, fetchAllData, isWorkspaceRoute, location.pathname, getWorkspaceIdFromURL, getProjectIdFromURL, currentWorkspace?.id, isDashboardHome]);

  const handleProjectCreated = (newProject) => {
    const transformedProject = {
      id: newProject.project_id,
      name: newProject.project_name,
      description: newProject.description,
      type: newProject.project_type || 'None',
      status: newProject.status || 'active',
      owner: newProject.owner || 'Unknown',
      createdAt: newProject.created_at,
      updatedAt: newProject.updated_at
    };

    setRecentProjects(prev => [transformedProject, ...prev.slice(0, 7)]);
  };

  const handleOpenProject = useCallback((project) => {
    const workspaceId = getWorkspaceIdFromURL() || currentWorkspace?.id;
    if (!workspaceId || !project?.id) {
      navigateTo('projects');
      return;
    }
    selectProject(String(project.id), project.name);
    updateWorkspaceFn({
      last_active_workspace_id: workspaceId,
      last_active_project_id: project.id,
    });
    navigate(buildWorkspaceDashboardPath(workspaceId, project.id));
  }, [getWorkspaceIdFromURL, currentWorkspace?.id, selectProject, updateWorkspaceFn, navigate, navigateTo]);

  const dashboardHome = useMemo(() => {
    // URL project segment is authoritative for Project Overview vs Workspace Home
    if (urlProjectId) {
      return (
        <ProjectOverview
          key={urlProjectId}
          projectName={activeProjectName || 'Project'}
          navigateTo={navigateTo}
        />
      );
    }

    return (
      <WorkspaceHome
        workspaceId={getWorkspaceIdFromURL() || currentWorkspace?.id}
        workspaceName={currentWorkspace?.workspace_name}
        dashboardData={dashboardData}
        activeProjects={activeProjects}
        loading={loading || isLoading}
        loadingProjects={loadingProjects}
        navigateTo={navigateTo}
        onOpenProject={handleOpenProject}
        userName={user?.name || user?.username || user?.fullName}
      />
    );
  }, [
    urlProjectId,
    activeProjectName,
    dashboardData,
    loading,
    isLoading,
    navigateTo,
    currentWorkspace?.workspace_name,
    currentWorkspace?.id,
    getWorkspaceIdFromURL,
    activeProjects,
    loadingProjects,
    handleOpenProject,
    user,
  ]);

const handleLogout = () => {
  sessionStorage.setItem('isLoggingOut', 'true');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('user');
  localStorage.removeItem('selectedWorkspace');
  localStorage.removeItem('selectedProject');

  queryClient.cancelQueries();
  queryClient.removeQueries();

  clearWorkspace();
  onLogout();
};

  const userString = localStorage.getItem('user');
  if (userString && !user) {
    setUser(JSON.parse(userString));
  }
  const handleViewProjectTestSuites = (projectId) => {
    setSelectedTestSuiteProjectId(projectId);
    navigateTo(`test-suites/details?project_id=${projectId}`);
  };


  const handleNavigateToTestSuite = (suiteId, suiteName, suiteData) => {
    setSelectedTestSuite(suiteData);
    setSelectedTestSuiteProjectId(suiteData.projectId || 1);
  };

  useEffect(() => {
    window.handleNavigateToTestSuite = handleNavigateToTestSuite;
    return () => {
      delete window.handleNavigateToTestSuite;
    };
  }, [handleNavigateToTestSuite]);

  return (
    <div className="dashboard-container rt2-dashboard">
      {!workspaceValidated && isWorkspaceRoute() ? (
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div className="loader">
            <i className='fas fa-spinner fa-spin' style={{ fontSize: '2rem' }}></i>
            <p>Loading workspace...</p>
          </div>
        </div>
      ) : (
        <AppShell
          user={user}
          onLogout={handleLogout}
          navigateTo={navigateTo}
          hasProjectContext={Boolean(urlProjectId)}
          projectId={activeProjectId}
          projectName={activeProjectName}
        >
          <ErrorBoundary key={location.pathname}>
            <DashboardRoutes
              dashboardHome={dashboardHome}
              navigateTo={navigateTo}
              handleViewProjectTestSuites={handleViewProjectTestSuites}
              selectedTestSuite={selectedTestSuite}
              setSelectedTestSuiteProjectId={setSelectedTestSuiteProjectId}
              fetchAllData={fetchAllData}
              location={location}
            />
          </ErrorBoundary>
        </AppShell>
      )}
    </div>
  );
};

export default Dashboard;